import React, { useCallback, useEffect } from "react";

import { endOfMonth, startOfMonth, startOfYear } from "date-fns";
import add from "date-fns/add";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import sub from "date-fns/sub";
import { Button as DateBoxButton, DateBox } from "devextreme-react/date-box";
import { run, sleep } from "effection";
import { Case, Switch } from "react-context-switch";
import { useDispatch, useSelector } from "react-redux";

import { runtimeParamsState, selectRuntimeParamsById } from "../../state";
import * as appConstants from "../../state/constants";
import { AppState, TParamDefinitionValues } from "../../types";
import "./params.scss";

import type { Operation } from "effection";
import type { ButtonType } from "devextreme/common";
const MonthNavigator = (props: {
  set: (d: Date) => void;
  get: () => Date;
  clss?: string;
}) => {
  const clss = props.clss || "";

  const prevDateButton = {
    icon: "spinprev",
    stylingMode: "text",
    onClick: () => {
      const a = sub(new Date(props.get()), { months: 1 });
      props.set(a);
    },
  };

  const nextDateButton = {
    icon: "spinnext",
    stylingMode: "text",
    onClick: () => {
      const a = add(new Date(props.get()), { months: 1 });
      props.set(a);
    },
  };

  const homeButton = {
    icon: "home",
    stylingMode: "text",
    onClick: () => {
      props.set(new Date(0));
    },
  };
  return (
    <div className="dx-buttongroup dx-widget">
      <DateBox
        className="dx-scheduler-navigator-caption"
        type="date"
        pickerType="calendar"
        calendarOptions={{
          maxZoomLevel: "year",
          minZoomLevel: "year",
          zoomLevel: "year",
          max: new Date(),
        }}
        displayFormat={"MMM yyyy"}
        value={props.get()}
      >
        <DateBoxButton
          name={"prevDate"}
          location={"before"}
          options={{
            elementAttr: { class: `${clss} wke-dtbx-btn-prev ` },
            icon: "spinprev",
            stylingMode: "text",
            onClick: () => {
              const a = sub(new Date(props.get()), { months: 1 });
              props.set(a);
            },
          }}
        />
        <DateBoxButton
          name={"nextDate"}
          location={"after"}
          options={{
            elementAttr: {
              class: `${clss} wke-dtbx-btn-next `,
              icon: "spinnext",
              stylingMode: "text",
              onClick: () => {
                const a = add(new Date(props.get()), { months: 1 });
                props.set(a);
              },
            },
          }}
        />
        {clss === "wke-dtbx-home" && (
          <DateBoxButton
            name={"home"}
            location={"after"}
            options={{
              elementAttr: { class: `${clss} wke-dtbx-btn-home ` },
              icon: "home",
              stylingMode: "text",
              onClick: () => {
                props.set(new Date(0));
              },
            }}
          />
        )}
      </DateBox>
    </div>
  );
};

export const PeriodPicker = ({
  showCase,
  pickerType,
}: {
  showCase: string;
  pickerType: TParamDefinitionValues;
}) => {
  const dispatch = useDispatch();

  const state = useSelector((s: AppState) =>
    selectRuntimeParamsById(s, showCase)
  );

  // select the params and dispatch them in order to build the querystring;
  const monthSetStart = (d: Date) => {
    run(setState("START", d));
  };

  const monthSetEnd = (d: Date) => {
    if (isSameDay(d, new Date(0))) {
      d = new Date();
      run(init());
      return;
    }
    run(setState("END", d));
  };

  const monthGetEnd = () => {
    return new Date(state?.endDate || new Date());
  };

  const monthGetStart = () => {
    return new Date(state?.startDate || new Date());
  };

  const setState = useCallback(
    (sender: "START" | "END" | never, d: Date | never) =>
      function* (): Operation<void> {
        yield* sleep(1);
        if (!state) return;
        const state0 = structuredClone(state);
        const newState = {
          ...state0,
          [sender === "START" ? "startDate" : "endDate"]: format(
            d,
            "yyyy-MM-dd"
          ),
        };
        // validate the dates
        const startDate = new Date(newState.startDate);
        const endDate = new Date(newState.endDate);

        //0. if the start date is after the end date then adjust the end date to be 1 month apart
        if (startDate > endDate) {
          if (sender === "START") {
            newState.endDate = format(
              add(endOfMonth(startDate), { months: 1 }),
              "yyyy-MM-dd"
            );
          }
          if (sender === "END") {
            newState.startDate = format(
              sub(startOfMonth(endDate), { months: 1 }),
              "yyyy-MM-dd"
            );
          }
        }
        const diff = Math.abs(startDate.getTime() - endDate.getTime());
        const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
        const diffMonths = Math.ceil(diffDays / 30);
        const diffYears = Math.ceil(diffMonths / 12);
        //1. if the distance between start and end is more than 1 year then adjust the other date to be 1 year apart
        if (diffYears > 1) {
          if (sender === "START") {
            newState.endDate = format(
              add(startDate, { years: 1 }),
              "yyyy-MM-dd"
            );
          }
          if (sender === "END") {
            newState.startDate = format(
              sub(endDate, { years: 1 }),
              "yyyy-MM-dd"
            );
          }
        }
        //2. if the distance between start and end is less than 1 month then adjust the other date to be 1 month apart
        const diffMonths2 = Math.ceil(diffDays / 30);
        if (Math.abs(diffMonths2) < 1) {
          if (sender === "START") {
            newState.endDate = format(
              add(startDate, { months: 1 }),
              "yyyy-MM-dd"
            );
          }
          if (sender === "END") {
            newState.startDate = format(
              sub(endDate, { months: 1 }),
              "yyyy-MM-dd"
            );
          }
        }
        dispatch(runtimeParamsState({ id: showCase, runtimeParams: newState }));
        yield* sleep(1);
        return;
      },
    [state]
  );

  const init = useCallback(
    () =>
      function* (): Operation<boolean> {
        yield* sleep(1);
        dispatch(
          runtimeParamsState({
            id: showCase,
            runtimeParams: {
              ...state,
              startDate: format(startOfYear(new Date()), "yyyy-MM-dd"),
              endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
            },
          })
        );
        return true;
      },
    []
  );

  useEffect(() => {
    run(init());
  }, []);

  return (
    <Switch value={pickerType}>
      <Case
        when={appConstants.ParamDefinitionValues.full_month_of_max_one_year}
      >
        <div className="swsx-flexband">
          <MonthNavigator set={monthSetStart} get={monthGetStart} />
          <MonthNavigator
            set={monthSetEnd}
            get={monthGetEnd}
            clss={"wke-dtbx-home"}
          />
        </div>
      </Case>
      <Case when={appConstants.ParamDefinitionValues.start_date_and_end_date}>
        <div>
          <DateBox />
          <DateBox />
        </div>
      </Case>
    </Switch>
  );
};
